import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const readOrder = () => {
  try { return JSON.parse(localStorage.getItem("ud_directory_order")) || []; } catch { return []; }
};

// Sequential Prev/Next navigator across the current subset order (ud_directory_order).
// mode: "view" -> {basePath}/{id}, "edit" -> {basePath}/{id}/edit. Supports ←/→ keys.
export default function RecordNav({ id, mode = "view", canLeave, basePath = "/alumni" }) {
  const navigate = useNavigate();
  const order = useMemo(readOrder, [id]);
  const idx = order.indexOf(id);
  const prevId = idx > 0 ? order[idx - 1] : null;
  const nextId = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;

  const go = useCallback((targetId) => {
    if (!targetId) return;
    if (canLeave && !canLeave()) return;
    // Replace history so Prev/Next don't stack — the Back arrow always returns to the original list.
    navigate(mode === "edit" ? `${basePath}/${targetId}/edit` : `${basePath}/${targetId}`, { replace: true });
  }, [navigate, mode, canLeave, basePath]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = (el?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || el?.isContentEditable) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); go(prevId); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(nextId); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, prevId, nextId]);

  if (idx === -1 || order.length <= 1) return null;

  return (
    <div className="flex items-center gap-2" data-testid="record-nav">
      <span data-testid="record-nav-position" className="text-xs text-muted-foreground mr-1 tabular-nums">{idx + 1} of {order.length}</span>
      <button data-testid="record-nav-prev" disabled={!prevId} onClick={() => go(prevId)} title="Previous record (←)" className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronLeft className="h-4 w-4" /> Prev
      </button>
      <button data-testid="record-nav-next" disabled={!nextId} onClick={() => go(nextId)} title="Next record (→)" className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        Next <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
